import { useEffect, useState } from "react";
import { getEkycReview, submitEkycDecision } from "./ekycReviewsApi.js";
import { startEkyc } from "../workerApplications/workerApplicationsApi.js";
import { createWorkerProfileFromEkycReview } from "../workerProfiles/workerProfilesApi.js";
import { EkycReviewDrawerContent, composeChecklistReason } from "./EkycReviewQueuePage.jsx";
import "./ekycReviewQueue.css";
import "../pages/states.css";

const START_EKYC_ERROR_TEXT = "เริ่มตรวจ e-KYC ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
const CREATE_PROFILE_ERROR_TEXT = "สร้าง Worker Profile ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
const CREATE_PROFILE_NO_REVIEW_TEXT = "ยังไม่มีเรคคอร์ด e-KYC สำหรับรายการนี้ จึงสร้าง Worker Profile ไม่ได้";

/**
 * P157: the full e-KYC review drawer, shared between the SuperAdmin e-KYC queue
 * page and the Agent Workers "รอ e-KYC" tab. It owns the whole controller -
 * detail fetch, the per-item check, the decision form, start-e-KYC and
 * create-Worker-Profile - and renders the exact same rich
 * [EkycReviewDrawerContent] the sysadmin screen has always used, so the Agent
 * gets documents, risk and the checklist rather than a bare approve/reject box.
 *
 * Driven by a single `queueRow` (an e-KYC queue item: workerApplicationId,
 * ekycReviewId, reviewRecordExists, applicationStatus). `onChanged` fires after
 * any real mutation so the host list can re-read (row status + counts).
 */
export default function EkycReviewDrawer({ queueRow, titleId, onChanged }) {
  const applicationId = queueRow?.workerApplicationId ?? null;
  const reviewId = queueRow?.ekycReviewId ?? null;

  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  const [decisionChoice, setDecisionChoice] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [decisionValidationError, setDecisionValidationError] = useState(null);
  const [decisionSubmit, setDecisionSubmit] = useState({ status: "idle", error: null });
  const [createProfileState, setCreateProfileState] = useState({ status: "idle", message: null, workerProfileId: null });
  const [startState, setStartState] = useState({ status: "idle", message: null });
  const [checkResults, setCheckResults] = useState({});

  // Opening a different application resets every per-item judgement and the
  // decision form - carrying one worker's answers onto another would be the
  // worst possible bug on this screen (the sysadmin page's own rule).
  useEffect(() => {
    setDecisionChoice(null);
    setReasonText("");
    setDecisionValidationError(null);
    setDecisionSubmit({ status: "idle", error: null });
    setCreateProfileState({ status: "idle", message: null, workerProfileId: null });
    setStartState({ status: "idle", message: null });
    setCheckResults({});
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId) {
      setDetailState({ status: "idle", data: null, error: null });
      return undefined;
    }
    // A row with no review record yet has no detail endpoint - the drawer
    // renders what the queue knows and offers "start e-KYC", which creates it.
    if (!queueRow?.reviewRecordExists || !reviewId) {
      setDetailState({ status: "ready", data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setDetailState((prev) => ({ status: "loading", data: prev.data, error: null }));
    getEkycReview(reviewId)
      .then((data) => {
        if (!cancelled) setDetailState({ status: "ready", data, error: null });
      })
      .catch((error) => {
        if (!cancelled) setDetailState({ status: "error", data: null, error });
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, reviewId, queueRow?.reviewRecordExists, reloadToken]);

  function handleCheckChange(key, value) {
    setCheckResults((current) => ({ ...current, [key]: value }));
  }

  async function handleStartEkyc() {
    if (!applicationId) return;
    setStartState({ status: "submitting", message: null });
    try {
      await startEkyc(applicationId);
      setStartState({ status: "idle", message: null });
      setReloadToken((t) => t + 1);
      onChanged?.();
    } catch (error) {
      setStartState({ status: "error", message: error?.body?.message ?? START_EKYC_ERROR_TEXT });
    }
  }

  async function handleCreateWorkerProfile() {
    if (!reviewId) {
      setCreateProfileState({ status: "error", message: CREATE_PROFILE_NO_REVIEW_TEXT, workerProfileId: null });
      return;
    }
    setCreateProfileState({ status: "submitting", message: null, workerProfileId: null });
    try {
      const created = await createWorkerProfileFromEkycReview(reviewId);
      setCreateProfileState({ status: "success", message: null, workerProfileId: created?.workerId ?? null });
      setReloadToken((t) => t + 1);
      onChanged?.();
    } catch (error) {
      setCreateProfileState({ status: "error", message: error?.body?.message ?? CREATE_PROFILE_ERROR_TEXT, workerProfileId: null });
    }
  }

  function handleProfileSaved() {
    setReloadToken((t) => t + 1);
    onChanged?.();
  }

  async function handleSubmitDecision(event) {
    event.preventDefault();
    if (!decisionChoice) {
      setDecisionValidationError("กรุณาเลือกการตัดสินใจก่อนบันทึก");
      return;
    }
    if (!reasonText.trim()) {
      setDecisionValidationError("กรุณาระบุเหตุผลก่อนบันทึกการตัดสินใจ");
      return;
    }
    setDecisionValidationError(null);
    setDecisionSubmit({ status: "submitting", error: null });
    try {
      await submitEkycDecision(reviewId, {
        decision: decisionChoice,
        reason: composeChecklistReason(checkResults, reasonText),
      });
      setDecisionSubmit({ status: "success", error: null });
      setDecisionChoice(null);
      setReasonText("");
      setReloadToken((t) => t + 1);
      onChanged?.();
    } catch (error) {
      setDecisionSubmit({ status: "error", error });
    }
  }

  return (
    <EkycReviewDrawerContent
      state={detailState}
      titleId={titleId}
      decisionChoice={decisionChoice}
      onSelectDecisionChoice={(choice) => {
        setDecisionChoice(choice);
        setDecisionValidationError(null);
      }}
      reasonText={reasonText}
      onReasonChange={setReasonText}
      decisionValidationError={decisionValidationError}
      decisionSubmit={decisionSubmit}
      onSubmitDecision={handleSubmitDecision}
      createProfileState={createProfileState}
      onCreateWorkerProfile={handleCreateWorkerProfile}
      onProfileSaved={handleProfileSaved}
      queueRow={queueRow}
      onStartEkyc={handleStartEkyc}
      startState={startState}
      checkResults={checkResults}
      onCheckChange={handleCheckChange}
    />
  );
}
